import {
  AuthError,
  type AuthTokens,
  type NewPasswordContinuation,
  type SignInResult,
  type TotpContinuation,
  type TotpSetupContinuation,
} from '@classgo/shared/auth';
import { Button, Input } from '@classgo/ui';
import { Link, useRouter, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { useAuthState, useAuthStore, useCognito } from '../../auth/auth-provider';
import { useKitT } from '../../i18n';
import { AuthLayout, FormError } from '../auth-layout';

type Step =
  | { kind: 'credentials' }
  | { kind: 'newPassword'; continuation: NewPasswordContinuation }
  | { kind: 'totp'; continuation: TotpContinuation }
  | { kind: 'totpSetup'; continuation: TotpSetupContinuation; secret: string | null };

const isInternalPath = (value: string | undefined): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

/**
 * Sign-in on ClassGO's own screens (ADR-0004): email and password, then whatever Cognito asks
 * next (permanent password on first login, TOTP code, TOTP enrollment). On success the person
 * lands where they were going, or on the home screen.
 */
export const LoginScreen = () => {
  const t = useKitT();
  const router = useRouter();
  const auth = useAuthStore();
  const cognito = useCognito();
  const { endedBy } = useAuthState();
  const search = useSearch({ strict: false }) as { redirect?: string | undefined };
  const redirect = search.redirect;
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = (tokens: AuthTokens) => {
    auth.completeSignIn(email.trim().toLowerCase(), tokens);
    router.history.push(isInternalPath(redirect) ? redirect : '/');
  };

  const fail = (thrown: unknown) =>
    setError(
      thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.UNKNOWN'),
    );

  const handle = (result: SignInResult) => {
    switch (result.kind) {
      case 'signedIn':
        finish(result.tokens);
        return;
      case 'newPasswordRequired':
        setStep({ kind: 'newPassword', continuation: result.continuation });
        return;
      case 'totpRequired':
        setStep({ kind: 'totp', continuation: result.continuation });
        return;
      case 'totpSetupRequired':
        setStep({ kind: 'totpSetup', continuation: result.continuation, secret: null });
        void result.continuation
          .begin()
          .then(({ secret }) =>
            setStep((current) => (current.kind === 'totpSetup' ? { ...current, secret } : current)),
          )
          .catch(fail);
    }
  };

  const run = async (action: () => Promise<SignInResult>) => {
    setBusy(true);
    setError(null);
    try {
      handle(await action());
    } catch (thrown) {
      fail(thrown);
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    switch (step.kind) {
      case 'credentials':
        void run(() => cognito.signIn(email.trim().toLowerCase(), password));
        return;
      case 'newPassword':
        if (newPassword !== confirmation) {
          setError(t('login.newPassword.mismatch'));
          return;
        }
        void run(() => step.continuation.complete(newPassword));
        return;
      case 'totp':
        void run(() => step.continuation.submit(code.trim()));
        return;
      case 'totpSetup':
        void run(() => step.continuation.verify(code.trim()));
    }
  };

  const titles = {
    credentials: [t('login.title'), t('login.subtitle')],
    newPassword: [t('login.newPassword.title'), t('login.newPassword.help')],
    totp: [t('login.totp.title'), t('login.totp.help')],
    totpSetup: [t('login.totpSetup.title'), t('login.totpSetup.help')],
  } as const;
  const [title, subtitle] = titles[step.kind];

  return (
    <AuthLayout
      title={title}
      subtitle={subtitle}
      footer={
        step.kind === 'credentials' ? (
          <Link
            to="/forgot-password"
            className="text-brand-600 underline underline-offset-2 hover:decoration-2"
          >
            {t('login.forgot')}
          </Link>
        ) : null
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {step.kind === 'credentials' && endedBy === 'expired' ? (
          <p role="status" className="rounded-control bg-info-100 px-3 py-2 text-sm text-info-700">
            {t('session.expired')}
          </p>
        ) : null}
        <FormError message={error} />
        {step.kind === 'credentials' ? (
          <>
            <Input
              label={t('login.email')}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              label={t('login.password')}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" loading={busy} className="w-full">
              {t('login.submit')}
            </Button>
          </>
        ) : null}
        {step.kind === 'newPassword' ? (
          <>
            <Input
              label={t('login.newPassword.field')}
              type="password"
              autoComplete="new-password"
              required
              help={t('password.policy')}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <Input
              label={t('login.newPassword.confirm')}
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <Button type="submit" loading={busy} className="w-full">
              {t('login.newPassword.submit')}
            </Button>
          </>
        ) : null}
        {step.kind === 'totp' || step.kind === 'totpSetup' ? (
          <>
            {step.kind === 'totpSetup' ? (
              <div className="rounded-control bg-gray-50 px-3 py-2 text-sm">
                <span className="block text-xs font-medium text-gray-500">
                  {t('login.totpSetup.secret')}
                </span>
                <code className="break-all text-gray-900">
                  {step.secret ?? t('common.loading')}
                </code>
              </div>
            ) : null}
            <Input
              label={t('login.totp.code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <Button type="submit" loading={busy} className="w-full">
              {step.kind === 'totp' ? t('login.totp.submit') : t('login.totpSetup.submit')}
            </Button>
          </>
        ) : null}
      </form>
    </AuthLayout>
  );
};
